import { useState } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";

const isVideo = (src) => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(src);

export function ExhibitionGallery({ title, images = [], fallback }) {
  const all = images.length > 0 ? images : fallback ? [fallback] : [];
  const [index, setIndex] = useState(0);
  if (all.length === 0) return null;
  const current = all[Math.min(index, all.length - 1)];
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <div data-testid={`gallery-${slug}`}>
      <div className="relative overflow-hidden bg-stone2">
        {isVideo(current) ? (
          <video
            key={current}
            src={current}
            controls
            playsInline
            preload="metadata"
            data-testid={`gallery-video-${slug}`}
            className="w-full aspect-[4/3] object-cover bg-ink"
            aria-label={`${title}, video ${index + 1} of ${all.length}`}
          />
        ) : (
          <img
            src={current}
            alt={`${title}, photo ${index + 1} of ${all.length}, curated by Seyoun Taylor`}
            loading="lazy"
            className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700"
          />
        )}
        {all.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo or video"
              data-testid={`gallery-prev-${slug}`}
              onClick={() => setIndex((i) => (i - 1 + all.length) % all.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/85 backdrop-blur-sm p-2 text-ink hover:bg-royal hover:text-white transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next photo or video"
              data-testid={`gallery-next-${slug}`}
              onClick={() => setIndex((i) => (i + 1) % all.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/85 backdrop-blur-sm p-2 text-ink hover:bg-royal hover:text-white transition-colors"
            >
              <ChevronRight size={18} />
            </button>
            <span className="absolute bottom-3 right-3 bg-ink/70 text-white text-xs px-2.5 py-1 tracking-wide">
              {index + 1} / {all.length}
            </span>
          </>
        )}
      </div>
      {all.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2" data-testid={`gallery-thumbs-${slug}`}>
          {all.map((src, i) => (
            <button
              key={src + i}
              type="button"
              aria-label={isVideo(src) ? `Play video ${i + 1}` : `View photo ${i + 1}`}
              data-testid={`gallery-thumb-${slug}-${i}`}
              onClick={() => setIndex(i)}
              className={`relative overflow-hidden bg-stone2 border-2 transition-colors ${
                i === index ? "border-royal" : "border-transparent hover:border-neutral-300"
              }`}
            >
              {isVideo(src) ? (
                <span className="flex w-full aspect-square items-center justify-center bg-ink text-white">
                  <Play size={18} />
                </span>
              ) : (
                <img src={src} alt="" loading="lazy" className="w-full aspect-square object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
